import rawPayers from '../stedi-payers-2026-03-24.json';

export interface Payer {
  name: string;
  id: string;
  stediId: string;
  coverageTypes: string[];
  operatingStates: string[];
  otherNames: number;
  namesList: string[];
  eligibility: boolean;
  cob: boolean;
  claim837P: boolean;
  claim837I: boolean;
  claim837D: boolean;
  claimAck: boolean;
  claimPayment: boolean;
  claimAttachments: boolean;
  realtimeClaim: boolean;
  avatarUrl: string;
}

interface RawPayer {
  StediId: string;
  PrimaryPayerId: string;
  DisplayName: string;
  Names: string;
  Aliases: string;
  EligibilityInquiry: string;
  ClaimStatusInquiry: string;
  ProfessionalClaim: string;
  InstitutionalClaim: string;
  DentalClaim: string;
  ClaimPaymentAdvice: string;
  CoordinationOfBenefits: string;
  ClaimAttachmentsSupport: string;
  CoverageTypes: string;
  OperatingStates: string;
  WebsiteUrl: string;
  [key: string]: string;
}

function toBool(val: string): boolean {
  return val === 'true';
}

export const payers: Payer[] = (rawPayers as RawPayer[]).map((r) => {
  const namesList = r.Names ? r.Names.split('|') : [];
  return {
    name: r.DisplayName,
    id: r.PrimaryPayerId,
    stediId: r.StediId,
    coverageTypes: r.CoverageTypes ? r.CoverageTypes.split('|') : [],
    operatingStates: r.OperatingStates ? r.OperatingStates.split('|') : [],
    otherNames: namesList.length,
    namesList,
    eligibility: toBool(r.EligibilityInquiry),
    cob: toBool(r.CoordinationOfBenefits),
    claim837P: toBool(r.ProfessionalClaim),
    claim837I: toBool(r.InstitutionalClaim),
    claim837D: toBool(r.DentalClaim),
    // 277CA acknowledgment is tied to claim submission support (837P/I/D)
    claimAck: toBool(r.ProfessionalClaim) || toBool(r.InstitutionalClaim) || toBool(r.DentalClaim),
    claimPayment: toBool(r.ClaimPaymentAdvice),
    claimAttachments: toBool(r.ClaimAttachmentsSupport),
    realtimeClaim: toBool(r.ClaimStatusInquiry),
    avatarUrl: '',
  };
});

// Filter options
export const COVERAGE_OPTIONS = [
  { key: 'dental', label: 'Dental' },
  { key: 'medical', label: 'Medical' },
  { key: 'vision', label: 'Vision' },
];

export const STATE_OPTIONS = [
  { key: 'NATIONAL', label: 'National' },
  { key: 'AL', label: 'Alabama' }, { key: 'AK', label: 'Alaska' }, { key: 'AS', label: 'American Samoa' },
  { key: 'AZ', label: 'Arizona' }, { key: 'AR', label: 'Arkansas' }, { key: 'CA', label: 'California' },
  { key: 'CO', label: 'Colorado' }, { key: 'CT', label: 'Connecticut' }, { key: 'DE', label: 'Delaware' },
  { key: 'DC', label: 'District of Columbia' }, { key: 'FL', label: 'Florida' }, { key: 'GA', label: 'Georgia' },
  { key: 'GU', label: 'Guam' }, { key: 'HI', label: 'Hawaii' }, { key: 'ID', label: 'Idaho' },
  { key: 'IL', label: 'Illinois' }, { key: 'IN', label: 'Indiana' }, { key: 'IA', label: 'Iowa' },
  { key: 'KS', label: 'Kansas' }, { key: 'KY', label: 'Kentucky' }, { key: 'LA', label: 'Louisiana' },
  { key: 'ME', label: 'Maine' }, { key: 'MD', label: 'Maryland' }, { key: 'MA', label: 'Massachusetts' },
  { key: 'MI', label: 'Michigan' }, { key: 'MN', label: 'Minnesota' }, { key: 'MS', label: 'Mississippi' },
  { key: 'MO', label: 'Missouri' }, { key: 'MT', label: 'Montana' }, { key: 'MP', label: 'Northern Mariana Islands' },
  { key: 'NE', label: 'Nebraska' }, { key: 'NV', label: 'Nevada' }, { key: 'NH', label: 'New Hampshire' },
  { key: 'NJ', label: 'New Jersey' }, { key: 'NM', label: 'New Mexico' }, { key: 'NY', label: 'New York' },
  { key: 'NC', label: 'North Carolina' }, { key: 'ND', label: 'North Dakota' }, { key: 'OH', label: 'Ohio' },
  { key: 'OK', label: 'Oklahoma' }, { key: 'OR', label: 'Oregon' }, { key: 'PA', label: 'Pennsylvania' },
  { key: 'PR', label: 'Puerto Rico' }, { key: 'RI', label: 'Rhode Island' }, { key: 'SC', label: 'South Carolina' },
  { key: 'SD', label: 'South Dakota' }, { key: 'TN', label: 'Tennessee' }, { key: 'TX', label: 'Texas' },
  { key: 'UT', label: 'Utah' }, { key: 'VT', label: 'Vermont' }, { key: 'VA', label: 'Virginia' },
  { key: 'WA', label: 'Washington' }, { key: 'WV', label: 'West Virginia' }, { key: 'WI', label: 'Wisconsin' },
  { key: 'WY', label: 'Wyoming' },
];

export const TRANSACTION_OPTIONS = [
  { key: 'eligibility', label: 'Eligibility checks', code: '270/271' },
  { key: 'cob', label: 'COB check', code: '271' },
  { key: 'claimSubmission', label: 'Claim submission', code: '837P, 837I, 837D' },
  { key: 'claimAck', label: 'Claim acknowledgment', code: '277CA' },
  { key: 'claimPayment', label: 'Claim payment', code: '835 ERA' },
  { key: 'claimAttachments', label: 'Claim attachments', code: '275' },
  { key: 'realtimeClaim', label: 'Real-time claim status', code: '276/277' },
];

// Column stats
export const STATS = {
  eligibility: payers.filter(p => p.eligibility).length,
  cob: payers.filter(p => p.cob).length,
  claimSubmission: payers.filter(p => p.claim837P || p.claim837I || p.claim837D).length,
  claimAck: payers.filter(p => p.claimAck).length,
  claimPayment: payers.filter(p => p.claimPayment).length,
  claimAttachments: payers.filter(p => p.claimAttachments).length,
  realtimeClaim: payers.filter(p => p.realtimeClaim).length,
};
